import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { Search, X } from 'lucide-react';

export interface MenuSearchInputHandle {
  focus: () => void;
  input: HTMLInputElement | null;
}

interface MenuSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  collapsed?: boolean;
  onCollapsedClick?: () => void;
  listboxId: string;
  isExpanded: boolean;
  activeDescendantId?: string;
}

const MenuSearchInput = forwardRef<MenuSearchInputHandle, MenuSearchInputProps>(
  (
    {
      value,
      onChange,
      onClear,
      onKeyDown,
      collapsed = false,
      onCollapsedClick,
      listboxId,
      isExpanded,
      activeDescendantId,
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
      input: inputRef.current,
    }));

    if (collapsed) {
      return (
        <div className="sidebar-search sidebar-search--collapsed">
          <button
            type="button"
            className="sidebar-search-collapsed-trigger collapse-button"
            aria-label="جستجو در داشبوردها"
            onClick={onCollapsedClick}
          >
            <Search size={18} aria-hidden="true" />
          </button>
        </div>
      );
    }

    return (
      <div className="sidebar-search" role="search">
        <div className="sidebar-search-field">
          <Search className="sidebar-search-icon" size={16} aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            className="sidebar-search-input"
            role="combobox"
            aria-label="جستجو در داشبوردها و گزارش‌ها"
            aria-expanded={isExpanded}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={activeDescendantId}
            placeholder="جستجو..."
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            autoComplete="off"
          />
          {value && (
            <button
              type="button"
              className="sidebar-search-clear"
              aria-label="پاک کردن جستجو"
              onClick={onClear}
            >
              <X size={14} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    );
  },
);

MenuSearchInput.displayName = 'MenuSearchInput';

export default MenuSearchInput;
